/**
 * Documents a client's portals brought in, under grey day rows (Today,
 * Yesterday, Earlier this week, Older), newest first (#15170, match-mockup
 * spec sections 2 and 4): Document (bold) | Manager (link) | Portal (logo and
 * link) | Type (tag) | Dated | Arrived (muted). The overview's Documents tab
 * draws every column; the manager page's Portals tab leaves out Manager.
 *
 * A row opens its document; the manager and portal links open their pages.
 * No state, no status column.
 */
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import type { PortalMarkOf } from "./PortalsOverview";
import { useViewerTimeZone } from "@/hooks/useViewerTimeZone";
import {
  formatCalendarDay,
  formatInstant,
  formatInstantTime,
} from "@/utils/temporal";
import {
  InlineLink,
  Mark,
  ROW_INTERACTION,
  listTd,
  listTh,
  managerPageHref,
  numTd,
} from "./PortalsOverview";
import { rowActivationProps } from "./rowActivation";
import type { DocumentRow } from "./browser/api";
import { TypeTagFor } from "./browser/DocumentsTable";
import { arrivalBucketOf, arrivalGroups } from "./browser/portals";

export interface PortalDocumentsTableProps {
  rows: DocumentRow[];
  /** The listed portals, to resolve a row's uploader to its logo and page. */
  services: PortalMarkOf[];
  typeLabels: Record<string, string>;
  /** Draw the Manager column (the overview's tab does; a manager's own
   *  page does not). */
  showManager?: boolean;
  onOpenFile: (row: DocumentRow) => void;
  /** A portal's page, by the service's name. */
  onPickService: (service: string) => void;
  /** One clock read for every row's day. */
  now: Date;
}

/** When a document arrived: the time today and yesterday, the weekday and
 *  time earlier this week, the day before that. */
const arrivedLabel = (at: string | null, zone: string, now: Date): string => {
  if (at === null) return "—";
  const bucket = arrivalBucketOf(at, zone, now);
  const time = formatInstantTime(at, zone);
  if (bucket === "today" || bucket === "yesterday") return time;
  if (bucket === "week")
    return `${formatInstant(at, zone, "weekdayName").slice(0, 3)} ${time}`;
  return formatInstant(at, zone, "monthDay");
};

const PortalDocumentsTable: React.FC<PortalDocumentsTableProps> = ({
  rows,
  services,
  typeLabels,
  showManager = true,
  onOpenFile,
  onPickService,
  now,
}) => {
  const zone = useViewerTimeZone();
  const navigate = useNavigate();
  const groups = useMemo(
    () => arrivalGroups(rows, zone, now),
    [rows, zone, now]
  );
  const byKey = useMemo(
    () => new Map(services.map((s) => [`system:${s.service}`, s])),
    [services]
  );
  const columns = showManager ? 6 : 5;
  return (
    <table className="w-full table-fixed border-collapse">
      <thead>
        <tr>
          <th scope="col" className={`${listTh} w-[30%]`}>
            Document
          </th>
          {showManager && (
            <th scope="col" className={`${listTh} w-[16%]`}>
              Manager
            </th>
          )}
          <th scope="col" className={`${listTh} w-[18%]`}>
            Portal
          </th>
          <th scope="col" className={`${listTh} w-[14%]`}>
            Type
          </th>
          <th scope="col" className={`${listTh} w-[9%]`}>
            Dated
          </th>
          <th scope="col" className={listTh}>
            Arrived
          </th>
        </tr>
      </thead>
      <tbody>
        {groups.map((group) => (
          <React.Fragment key={group.bucket}>
            <tr data-testid="day-row" className="bg-[var(--fp-bg-well)]">
              <td
                colSpan={columns}
                className="border-b border-[var(--fp-border)] px-3 py-1.5 text-[12px] font-medium text-[var(--fp-ink-3)]"
              >
                {group.label}
              </td>
            </tr>
            {group.rows.map((row) => {
              const manager = row.linkedEntities.find(
                (e) => e.kind === "manager"
              );
              const service = row.uploadedBy
                ? byKey.get(row.uploadedBy.key)
                : undefined;
              const type = row.documentTypes[0];
              return (
                <tr
                  key={row.fileId}
                  data-file={row.fileId}
                  className={`border-b border-[var(--fp-border)] ${ROW_INTERACTION}`}
                  {...rowActivationProps(() => onOpenFile(row))}
                >
                  <td className={`${listTd} font-semibold`}>
                    <span className="block truncate">{row.fileName}</span>
                  </td>
                  {showManager && (
                    <td className={listTd}>
                      {manager ? (
                        <InlineLink
                          onOpen={() =>
                            navigate(
                              managerPageHref({
                                name: manager.name,
                                publicId: manager.publicId,
                              })
                            )
                          }
                        >
                          {manager.name}
                        </InlineLink>
                      ) : (
                        <span className="text-[var(--fp-ink-3)]">—</span>
                      )}
                    </td>
                  )}
                  <td className={listTd}>
                    {service ? (
                      <span className="flex min-w-0 items-center gap-2">
                        <span
                          className="flex shrink-0"
                          data-testid="portal-logo"
                        >
                          <Mark service={service} size={16} />
                        </span>
                        <InlineLink
                          onOpen={() => onPickService(service.service)}
                        >
                          {service.service}
                        </InlineLink>
                      </span>
                    ) : (
                      <span className="text-[var(--fp-ink-3)]">—</span>
                    )}
                  </td>
                  <td className={listTd}>
                    {type ? (
                      <TypeTagFor typeKey={type} typeLabels={typeLabels} />
                    ) : null}
                  </td>
                  <td className={`${listTd} ${numTd}`}>
                    {row.documentDate
                      ? formatCalendarDay(row.documentDate, "monthDay")
                      : "—"}
                  </td>
                  <td className={`${listTd} ${numTd} text-[var(--fp-ink-3)]`}>
                    {arrivedLabel(row.uploadedAt, zone, now)}
                  </td>
                </tr>
              );
            })}
          </React.Fragment>
        ))}
      </tbody>
    </table>
  );
};

export default PortalDocumentsTable;
